<template><section><!-- ---
title: JS深入系列之New做了什么
date: 2022-10-21
tags: JS深入系列
set: DeepJS
--- -->
<blockquote>
<p>new运算符是创建一个用户定义的对象类型的实例，或具有构造函数的内置对象类型之一</p>
</blockquote>
<h3>1. new做了什么</h3>
<p>换而言之，就是<code v-pre>new</code>就是为构造函数创建一个对象实例，我们来通过一个例子了解一下<code v-pre>new</code>实现了哪些功能：</p>
<pre v-pre><code v-pre class="language-javascript"><span class="hljs-function"><span class="hljs-keyword">function</span> <span class="hljs-title">Func</span>(<span class="hljs-params">name, age</span>) </span>{
    <span class="hljs-keyword">this</span>.name = name
    <span class="hljs-keyword">this</span>.age = age
    <span class="hljs-keyword">this</span>.habit = <span class="hljs-string">'shopping'</span>
}
Func.prototype.job = <span class="hljs-string">'programming'</span>
Func.prototype.say = <span class="hljs-function"><span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) </span>{
    <span class="hljs-built_in">console</span>.log(<span class="hljs-string">'I am '</span>, <span class="hljs-keyword">this</span>.name)
}
<span class="hljs-keyword">const</span> obj = <span class="hljs-keyword">new</span> Func(<span class="hljs-string">'Gemma'</span>, <span class="hljs-number">18</span>)
<span class="hljs-built_in">console</span>.log(obj.name)  <span class="hljs-comment">// Gemma</span>
<span class="hljs-built_in">console</span>.log(obj.age)   <span class="hljs-comment">// 18</span>
<span class="hljs-built_in">console</span>.log(obj.habit) <span class="hljs-comment">// shopping</span>
<span class="hljs-built_in">console</span>.log(obj.job)   <span class="hljs-comment">// programming</span>
obj.say()              <span class="hljs-comment">// I am Gemma</span>
</code></pre>
<p>我们可以看到<code v-pre>new</code>创建的实例可以：</p>
<ul>
<li>访问到构造函数内部的属性</li>
<li>访问到构造函数原型上的属性</li>
</ul>
<h3>2. 手写new</h3>
<p>因此我们就来手写一个<code v-pre>new</code>，这里没有办法在任何东西的原型上去定义<code v-pre>new</code>，我们可以把<code v-pre>new</code>写做一个函数<code v-pre>objectFactory</code>，来模拟<code v-pre>new</code>的效果。手写的整个过程是：</p>
<ul>
<li><code v-pre>objectFactory</code>的第一个参数取出来作为构造函数</li>
<li><code v-pre>objectFactory</code>内新建一个对象<code v-pre>obj</code>，将对象<code v-pre>obj</code>的<code v-pre>__proto</code>指向构造函数的原型，这样<code v-pre>obj</code>就可以访问构造函数原型上的属性</li>
<li>使用<code v-pre>apply</code>，改变构造函数的<code v-pre>this</code>到<code v-pre>obj</code>，这样<code v-pre>obj</code>就可以访问到构造函数的属性</li>
<li>将除第一个参数之外的参数作为参数传入构造函数</li>
</ul>
<pre v-pre><code v-pre class="language-javascript"><span class="hljs-function"><span class="hljs-keyword">function</span> <span class="hljs-title">objectFactory</span>(<span class="hljs-params"></span>) </span>{
    <span class="hljs-keyword">const</span> Constructor = [].shift.call(<span class="hljs-built_in">arguments</span>)
    <span class="hljs-keyword">const</span> obj = <span class="hljs-keyword">new</span> <span class="hljs-built_in">Object</span>()
    obj.__proto__ = Constructor.prototype
    <span class="hljs-keyword">return</span> Constructor.apply(obj, <span class="hljs-built_in">arguments</span>)
}
</code></pre>
<p>初版的测试用例</p>
<pre v-pre><code v-pre class="language-javascript"><span class="hljs-function"><span class="hljs-keyword">function</span> <span class="hljs-title">Func</span>(<span class="hljs-params">name, age</span>) </span>{
    <span class="hljs-keyword">this</span>.name = name
    <span class="hljs-keyword">this</span>.age = age
    <span class="hljs-keyword">this</span>.habit = <span class="hljs-string">'shopping'</span>
}
Func.prototype.job = <span class="hljs-string">'programming'</span>
Func.prototype.say = <span class="hljs-function"><span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) </span>{
    <span class="hljs-built_in">console</span>.log(<span class="hljs-string">'I am '</span>, <span class="hljs-keyword">this</span>.name)
}
<span class="hljs-keyword">const</span> ob1 = objectFactory(Func, <span class="hljs-string">'Gemma'</span>, <span class="hljs-number">18</span>)
<span class="hljs-built_in">console</span>.log(ob1.name)  <span class="hljs-comment">// Gemma</span>
<span class="hljs-built_in">console</span>.log(ob1.age)   <span class="hljs-comment">// 18</span>
<span class="hljs-built_in">console</span>.log(ob1.habit) <span class="hljs-comment">// shopping</span>
<span class="hljs-built_in">console</span>.log(ob1.job)   <span class="hljs-comment">// programming</span>
ob1.say()              <span class="hljs-comment">// I am Gemma</span>
</code></pre>
<p>这里，我们再来看看<code v-pre>new</code>的用法，当构造函数返回一个东西的时候，会怎么样呢？</p>
<ul>
<li><strong>返回对象</strong>：如果构造函数返回一个对象，则实例只能访问到返回的对象属性，访问不到原型中的属性</li>
<li><strong>返回基本类型</strong>：如果构造函数返回一个基本类型的值，那实例就只能访问到构造函数的属性，也可以访问到原型中的属性</li>
</ul>
<pre v-pre><code v-pre class="language-javascript"><span class="hljs-comment">// 返回一个对象</span>
<span class="hljs-function"><span class="hljs-keyword">function</span> <span class="hljs-title">Fn</span>(<span class="hljs-params">name, age</span>) </span>{
    <span class="hljs-keyword">this</span>.name = name
    <span class="hljs-keyword">this</span>.age = age
    <span class="hljs-keyword">this</span>.habit = <span class="hljs-string">'shopping'</span>
    <span class="hljs-keyword">return</span> {
        name,
        <span class="hljs-attr">address</span>: <span class="hljs-string">'China'</span>
    }
}
Fn.prototype.job = <span class="hljs-string">'programming'</span>
Fn.prototype.sayName = <span class="hljs-function"><span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) </span>{
  <span class="hljs-built_in">console</span>.error(<span class="hljs-string">'I am'</span>, <span class="hljs-keyword">this</span>.name)
}
<span class="hljs-keyword">var</span> person = <span class="hljs-keyword">new</span> Fn(<span class="hljs-string">'Gemma'</span>, <span class="hljs-string">'18'</span>)
<span class="hljs-built_in">console</span>.log(person.name)   <span class="hljs-comment">// Gemma</span>
<span class="hljs-built_in">console</span>.log(person.age)    <span class="hljs-comment">// undefined</span>
<span class="hljs-built_in">console</span>.log(person.habit)  <span class="hljs-comment">// undeined</span>
<span class="hljs-built_in">console</span>.log(person.job)    <span class="hljs-comment">// undefined</span>
<span class="hljs-built_in">console</span>.log(person.address)<span class="hljs-comment">// China</span>
person.sayName()          <span class="hljs-comment">// sayName is not function</span>
</code></pre>
<pre v-pre><code v-pre class="language-javascript"><span class="hljs-comment">// 返回一个基本类型</span>
<span class="hljs-function"><span class="hljs-keyword">function</span> <span class="hljs-title">Fn</span>(<span class="hljs-params">name, age</span>) </span>{
    <span class="hljs-keyword">this</span>.name = name
    <span class="hljs-keyword">this</span>.age = age
    <span class="hljs-keyword">this</span>.habit = <span class="hljs-string">'shopping'</span>
    <span class="hljs-keyword">return</span> <span class="hljs-number">1</span>
}
Fn.prototype.job = <span class="hljs-string">'programming'</span>
Fn.prototype.sayName = <span class="hljs-function"><span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) </span>{
  <span class="hljs-built_in">console</span>.error(<span class="hljs-string">'I am'</span>, <span class="hljs-keyword">this</span>.name)
}
<span class="hljs-keyword">var</span> person = <span class="hljs-keyword">new</span> Fn(<span class="hljs-string">'Gemma'</span>, <span class="hljs-string">'18'</span>)
<span class="hljs-built_in">console</span>.log(person.name)   <span class="hljs-comment">// Gemma</span>
<span class="hljs-built_in">console</span>.log(person.age)    <span class="hljs-comment">// 18</span>
<span class="hljs-built_in">console</span>.log(person.habit)  <span class="hljs-comment">// shopping</span>
<span class="hljs-built_in">console</span>.log(person.job)    <span class="hljs-comment">// programming</span>
person2.sayName()          <span class="hljs-comment">// I am Gemma</span>
</code></pre>
<p>我们来修正一下手写<code v-pre>new</code>的实现</p>
<pre v-pre><code v-pre class="language-javascript"><span class="hljs-function"><span class="hljs-keyword">function</span> <span class="hljs-title">objectFactory</span>(<span class="hljs-params"></span>) </span>{
    <span class="hljs-keyword">const</span> Constructor = [].shift.call(<span class="hljs-built_in">arguments</span>)
    <span class="hljs-keyword">const</span> obj = <span class="hljs-keyword">new</span> <span class="hljs-built_in">Object</span>()
    obj.__proto__ = Constructor.prototype
    <span class="hljs-keyword">const</span> ret = Constructor.apply(obj, <span class="hljs-built_in">arguments</span>)
    <span class="hljs-keyword">if</span> (<span class="hljs-keyword">typeof</span> ret == <span class="hljs-string">'Object'</span>) {
        <span class="hljs-keyword">return</span> ret
    } <span class="hljs-keyword">else</span> {
        <span class="hljs-keyword">return</span> obj
    }
}
</code></pre>
</section></template>

